import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Megaphone,
  Play,
  Pause,
  Trash2,
  Plus,
  Search,
  Download,
  ListChecks
} from 'lucide-react';
import broadcastService from '../../services/whatsapp/broadcastService';
import {
  Broadcast,
  BroadcastStatus,
  readRate,
  responseRate,
  successRate
} from '../../types/models/whatsapp';
import NewCampaignDialog from './broadcasts/NewCampaignDialog';
import RecipientsDialog from './broadcasts/RecipientsDialog';

const formatDate = (timestamp: number | null): string =>
  timestamp
    ? new Date(timestamp).toLocaleString([], {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    : '—';

const STATUS_TONE: Record<BroadcastStatus, string> = {
  draft: 'bg-gray-100 text-gray-600',
  scheduled: 'bg-primary-50 text-primary-700',
  running: 'bg-warning-50 text-warning-700',
  paused: 'bg-warning-50 text-warning-700',
  completed: 'bg-success-50 text-success-600',
  failed: 'bg-danger-50 text-danger-600',
  cancelled: 'bg-gray-100 text-gray-500'
};

const percent = (value: number): string => `${(value * 100).toFixed(1)}%`;

/**
 * Bulk Messaging — the campaign list.
 *
 * Counters are read live from Firestore, so a running campaign's numbers climb
 * without a refresh.
 */
const WhatsAppBroadcasts: React.FC = () => {
  const [broadcasts, setBroadcasts] = useState<Broadcast[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [newOpen, setNewOpen] = useState(false);
  const [recipientsFor, setRecipientsFor] = useState<Broadcast | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(
    () =>
      broadcastService.subscribe(
        (result) => {
          setBroadcasts(result);
          setLoading(false);
        },
        (err) => {
          setError(err.message);
          setLoading(false);
        }
      ),
    []
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return broadcasts;
    return broadcasts.filter(
      (b) =>
        b.name.toLowerCase().includes(term) ||
        b.templateName.toLowerCase().includes(term)
    );
  }, [broadcasts, search]);

  /** Aggregate KPIs across the filtered campaigns. */
  const totals = useMemo(() => {
    return filtered.reduce(
      (acc, b) => ({
        total: acc.total + b.stats.total,
        delivered: acc.delivered + b.stats.delivered,
        read: acc.read + b.stats.read,
        responded: acc.responded + b.stats.responded
      }),
      { total: 0, delivered: 0, read: 0, responded: 0 }
    );
  }, [filtered]);

  const act = useCallback(
    async (id: string, action: () => Promise<void>) => {
      setBusyId(id);
      setError(null);
      try {
        await action();
      } catch (err: any) {
        setError(err.message || 'Action failed');
      } finally {
        setBusyId(null);
      }
    },
    []
  );

  const handleDelete = useCallback(
    (broadcast: Broadcast) => {
      // No window.confirm — a browser dialog blocks the page. The button is
      // only enabled for stopped campaigns, and deletion is explicit.
      act(broadcast.id, () => broadcastService.remove(broadcast.id));
    },
    [act]
  );

  const handleExport = useCallback(() => {
    const header = [
      'Campaign',
      'Description',
      'Status',
      'Template',
      'Total',
      'Waiting',
      'Sent',
      'Delivered',
      'Read',
      'Failed',
      'Responded',
      'Skipped',
      'Created'
    ];
    const rows = filtered.map((b) => [
      b.name,
      b.description,
      b.status,
      b.templateName,
      b.stats.total,
      b.stats.waiting,
      b.stats.sent,
      b.stats.delivered,
      b.stats.read,
      b.stats.failed,
      b.stats.responded,
      b.stats.skipped,
      formatDate(b.createdAt)
    ]);

    const csv = [header, ...rows]
      .map((r) =>
        r
          .map((cell) => {
            const text = String(cell ?? '');
            return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
          })
          .join(',')
      )
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `whatsapp-campaigns-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [filtered]);

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 bg-white p-3">
        <h1 className="mr-2 flex items-center gap-2 text-base font-semibold text-gray-900">
          <Megaphone className="h-5 w-5" />
          Bulk Messaging
        </h1>

        <div className="relative min-w-[14rem] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search campaigns or templates"
            className="w-full rounded-lg border-gray-300 pl-9 text-sm focus:border-primary-500 focus:ring-primary-500"
          />
        </div>

        <button
          type="button"
          onClick={handleExport}
          disabled={filtered.length === 0}
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <Download className="h-3.5 w-3.5" />
          Export
        </button>

        <button
          type="button"
          onClick={() => setNewOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-700"
        >
          <Plus className="h-3.5 w-3.5" />
          New campaign
        </button>
      </div>

      {/* KPI tiles. Denominators match the analytics definitions: success is
          over total, read and response over delivered. */}
      <div className="grid grid-cols-2 gap-3 border-b border-gray-200 bg-gray-50 p-3 md:grid-cols-4">
        {[
          { label: 'Total messages', value: String(totals.total) },
          {
            label: 'Success rate',
            value: percent(successRate({ ...totals } as any)),
            hint: 'Delivered / Total'
          },
          {
            label: 'Read rate',
            value: percent(readRate({ ...totals } as any)),
            hint: 'Read / Delivered'
          },
          {
            label: 'Response rate',
            value: percent(responseRate({ ...totals } as any)),
            hint: 'Responded / Delivered'
          }
        ].map((tile) => (
          <div
            key={tile.label}
            className="rounded-lg border border-gray-200 bg-white p-3"
          >
            <p className="text-xs text-gray-500">{tile.label}</p>
            <p className="mt-1 text-xl font-semibold text-gray-900">
              {tile.value}
            </p>
            {tile.hint && (
              <p className="text-[10px] text-gray-400">{tile.hint}</p>
            )}
          </div>
        ))}
      </div>

      {error && (
        <div className="bg-danger-50 px-4 py-2 text-sm text-danger-600">
          {error}
          <button
            type="button"
            onClick={() => setError(null)}
            className="ml-2 font-medium underline"
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-auto">
        <table className="w-full border-collapse text-sm">
          <thead className="sticky top-0 z-10 bg-gray-50">
            <tr className="text-left text-xs font-medium text-gray-600">
              <th className="px-3 py-2.5">Sr No.</th>
              <th className="px-3 py-2.5">Campaign</th>
              <th className="px-3 py-2.5">Status</th>
              <th className="px-3 py-2.5">Template</th>
              <th className="px-3 py-2.5 text-right">Waiting</th>
              <th className="px-3 py-2.5 text-right">Sent</th>
              <th className="px-3 py-2.5 text-right">Delivered</th>
              <th className="px-3 py-2.5 text-right">Read</th>
              <th className="px-3 py-2.5 text-right">Failed</th>
              <th className="px-3 py-2.5 text-right">Responded</th>
              <th className="px-3 py-2.5 text-right">Skipped</th>
              <th className="px-3 py-2.5 text-right">Total</th>
              <th className="px-3 py-2.5">Created</th>
              <th className="px-3 py-2.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={14} className="px-3 py-8 text-center text-gray-500">
                  Loading campaigns…
                </td>
              </tr>
            )}

            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={14} className="px-3 py-12 text-center">
                  <Megaphone className="mx-auto h-10 w-10 text-gray-300" />
                  <p className="mt-2 text-sm font-medium text-gray-500">
                    {broadcasts.length === 0 ? 'No campaigns yet' : 'No matches'}
                  </p>
                  <p className="mt-1 text-xs text-gray-400">
                    {broadcasts.length === 0
                      ? 'Create a campaign to send an approved template to many contacts.'
                      : 'Try a different search.'}
                  </p>
                </td>
              </tr>
            )}

            {!loading &&
              filtered.map((broadcast, index) => {
                const canStart = ['draft', 'scheduled', 'paused'].includes(
                  broadcast.status
                );
                const canStop = broadcast.status === 'running';
                const canDelete = !canStop;

                return (
                  <tr
                    key={broadcast.id}
                    className="border-b border-gray-100 hover:bg-gray-50"
                  >
                    <td className="px-3 py-2.5 text-gray-500">{index + 1}</td>
                    <td className="px-3 py-2.5">
                      <span className="block font-medium text-gray-900">
                        {broadcast.name}
                      </span>
                      {broadcast.description && (
                        <span className="block text-xs text-gray-500">
                          {broadcast.description}
                        </span>
                      )}
                      {broadcast.error && (
                        <span className="block text-xs text-danger-600">
                          {broadcast.error}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <span
                        className={`rounded-full px-2 py-0.5 text-[11px] font-medium capitalize ${
                          STATUS_TONE[broadcast.status]
                        }`}
                      >
                        {broadcast.status}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-gray-600">
                      {broadcast.templateName}
                    </td>
                    <td className="px-3 py-2.5 text-right text-gray-500">
                      {broadcast.stats.waiting}
                    </td>
                    <td className="px-3 py-2.5 text-right text-gray-700">
                      {broadcast.stats.sent}
                    </td>
                    <td className="px-3 py-2.5 text-right text-gray-700">
                      {broadcast.stats.delivered}
                    </td>
                    <td className="px-3 py-2.5 text-right text-primary-600">
                      {broadcast.stats.read}
                    </td>
                    <td className="px-3 py-2.5 text-right text-danger-600">
                      {broadcast.stats.failed}
                    </td>
                    <td className="px-3 py-2.5 text-right text-success-600">
                      {broadcast.stats.responded}
                    </td>
                    <td className="px-3 py-2.5 text-right text-gray-400">
                      {broadcast.stats.skipped}
                    </td>
                    <td className="px-3 py-2.5 text-right font-medium text-gray-900">
                      {broadcast.stats.total}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-gray-500">
                      {formatDate(broadcast.createdAt)}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => setRecipientsFor(broadcast)}
                          className="rounded p-1.5 text-gray-500 hover:bg-gray-100"
                          title="View recipients"
                          aria-label="View recipients"
                        >
                          <ListChecks className="h-4 w-4" />
                        </button>

                        {canStart && (
                          <button
                            type="button"
                            disabled={busyId === broadcast.id}
                            onClick={() =>
                              act(broadcast.id, () =>
                                broadcastService.start(broadcast.id)
                              )
                            }
                            className="rounded p-1.5 text-success-600 hover:bg-gray-100 disabled:opacity-40"
                            title="Start sending"
                            aria-label="Start sending"
                          >
                            <Play className="h-4 w-4" />
                          </button>
                        )}

                        {canStop && (
                          <button
                            type="button"
                            disabled={busyId === broadcast.id}
                            onClick={() =>
                              act(broadcast.id, () =>
                                broadcastService.pause(broadcast.id)
                              )
                            }
                            className="rounded p-1.5 text-warning-600 hover:bg-gray-100 disabled:opacity-40"
                            title="Pause"
                            aria-label="Pause"
                          >
                            <Pause className="h-4 w-4" />
                          </button>
                        )}

                        <button
                          type="button"
                          disabled={busyId === broadcast.id || !canDelete}
                          onClick={() => handleDelete(broadcast)}
                          className="rounded p-1.5 text-gray-400 hover:bg-danger-50 hover:text-danger-600 disabled:opacity-30"
                          title={
                            canDelete ? 'Delete' : 'Stop the campaign first'
                          }
                          aria-label="Delete campaign"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      <NewCampaignDialog
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onCreated={() => setNewOpen(false)}
      />

      <RecipientsDialog
        broadcast={recipientsFor}
        onClose={() => setRecipientsFor(null)}
      />
    </div>
  );
};

export default WhatsAppBroadcasts;
